import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useCompanyRejectAssetRequestMutation } from "@/services/(company)/api.assets";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useState, type ReactNode } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { XCircle } from "lucide-react";

interface Props {
  children: ReactNode;
  requestId: number;
}

const buildFormSchema = (t: TFunction) =>
  yup.object({
    rejectionReason: yup
      .string()
      .required(t("assets:rejection_reason_is_required")),
  });
type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;

export const ActionRejectAssetReqDialog = ({ children, requestId }: Props) => {
  const { t } = useTranslation(["assets", "common"]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [rejectRequest, { isLoading }] = useCompanyRejectAssetRequestMutation();

  const formik = useFormik<Fields>({
    initialValues: {
      rejectionReason: "",
    },
    validationSchema: buildFormSchema(t),
    onSubmit: async ({ rejectionReason }) => {
      const id = toast.loading(t("assets:processing"));
      await rejectRequest({ requestId, rejectionReason })
        .unwrap()
        .then((res) => {
          toast.success(res.message, { id });
          setIsOpen(false);
        })
        .catch((e) =>
          toast.error(e?.data?.errors?.message ?? e?.data?.message, { id }),
        );
    },
  });

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        formik.resetForm();
        setIsOpen(open);
      }}
    >
      <AlertDialogTrigger
        asChild
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={t("assets:reject_this_asset_request")}
      >
        {children}
      </AlertDialogTrigger>
      <AlertDialogContent asChild className="gap-0 p-0 overflow-hidden">
        <form
          onSubmit={formik.handleSubmit}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Colored header band */}
          <div className="bg-danger-muted px-6 pt-6 pb-5 flex gap-4 items-start border-b border-danger">
            <div className="shrink-0 flex items-center justify-center size-10 rounded-full bg-danger">
              <XCircle className="text-danger size-5" />
            </div>
            <AlertDialogHeader className="space-y-1 text-start">
              <AlertDialogTitle className="text-base text-danger-700">
                {t("assets:reject_asset_request")}
              </AlertDialogTitle>
              <p className="text-sm text-danger/80">
                {t(
                  "assets:this_action_cannot_be_undone_the_employee_will_be_notifi",
                )}
              </p>
            </AlertDialogHeader>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <Field>
              <FieldLabel htmlFor={"rejectionReason" satisfies keyof Fields}>
                {t("assets:rejection_reason")}
                <span className="text-danger">*</span>
              </FieldLabel>
              <Textarea
                id={"rejectionReason" satisfies keyof Fields}
                className="min-h-28 mt-1.5 resize-none"
                onChange={formik.handleChange}
                value={formik.values.rejectionReason}
                placeholder={t("assets:describe_the_reason_for_rejection")}
                name={"rejectionReason" satisfies keyof Fields}
              />
              <FieldError>{formik.errors.rejectionReason}</FieldError>
            </Field>
          </div>

          <AlertDialogFooter className="px-6 pb-5 flex gap-3">
            <AlertDialogCancel
              type="reset"
              className="flex-1"
              disabled={isLoading}
            >
              {t("common:buttons.cancel")}
            </AlertDialogCancel>
            <Button
              type="submit"
              disabled={isLoading}
              variant="danger"
              className="flex-1"
            >
              {isLoading ? t("assets:rejecting") : t("assets:reject_request")}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
